import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { Consultant } from '../../interfaces/Consultant';
import { dataActionCreators, Invoice, Project, ProjectAssigment, TimeSheetEntry } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useEditorStyles } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Text, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { useConsultants, useCustomers, useInvoices, useProjects, useProjectsAssigments } from '../Tools/ObjectLoader';

export const TimeSheetDeleteConfirm = () => {
    // All data needed for edition should be loaded before calling the editor
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const timeSheetDelete = useSelector<ApplicationState, TimeSheetEntry | null>((state: ApplicationState) => state.data.timeSheetDelete);
    const { customers } = useCustomers();
    const { consultants } = useConsultants();
    const { projectsAssigments } = useProjectsAssigments();
    const { projects } = useProjects();
    const { invoices } = useInvoices();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Form validation
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    // Dependencies
    const projectAssignment: ProjectAssigment | undefined = React.useMemo(
        () => {
            return timeSheetDelete && timeSheetDelete.projectAssignmentid && projectsAssigments ? projectsAssigments.find(x => timeSheetDelete && x.projectAssignmentid === timeSheetDelete.projectAssignmentid) : undefined;
        }, [timeSheetDelete, projectsAssigments]);
    const project: Project | undefined = React.useMemo(
        () => {
            return projects && projectAssignment ? projects.find((c) => c.projectid === projectAssignment.projectid) : undefined;
        }, [projects, projectAssignment]);
    const customer: Customer | undefined = React.useMemo(
        () => {
            return customers && project && projects ? customers.find((c) => project && c.customerid === project.customerid) : undefined;
        }, [customers, project]);
    const consultant: Consultant | undefined = React.useMemo(
        () => {
            return consultants && projectAssignment ? consultants.find((c) => projectAssignment && c.consultantid === projectAssignment.consultantid) : undefined;
        }, [consultants, projectAssignment]);
    const invoice: Invoice | undefined = React.useMemo(
        () => {
            return timeSheetDelete && invoices ? invoices.find((c) => timeSheetDelete && c.invoiceid === timeSheetDelete.invoiceid) : undefined;
        }, [invoices, timeSheetDelete]);

    const getPriceTypeSelectionString = (s: string): string => {
        switch (s) {
            case "H": return t('Hours');
            case "D": return t('Days');
            case "M": return t('Month');
            case "F": return t('Fixed');
        }
        return "Unknown";
    };

    if (timeSheetDelete === null) {
        return <></>;
    }

    const submit = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                {i18n.t('DeletingTimeSheet', { ns: "Toasts" })}
                            </ToastTitle>
                            <ToastBody>
                                <div className={styles.Vertical}>
                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                    <Text>{(timeSheetDelete ? timeSheetDelete.count : "") + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                </div>
                            </ToastBody>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/TimeSheets/" + (timeSheetDelete && timeSheetDelete.timesheetEntryid !== null ? timeSheetDelete.timesheetEntryid : ""), {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingTimeSheetSucceed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                                    <Text>{(timeSheetDelete ? timeSheetDelete.count : "") + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                if (timeSheetDelete) dispatch(dataActionCreators.patchTimeSheetDeletionEntryEdition(timeSheetDelete));
                                dispatch(dataActionCreators.requestTimeSheetEntrydeletion(null));
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingTimeSheetFailed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                                    <Text>{(timeSheetDelete ? timeSheetDelete.count : "") + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>Delete timesheet</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestTimeSheetEntrydeletion(null));
                        }}
                        autoComplete="off"
                    >
                        <Field label={t('Consultant')}>
                            <Label className={styles.label}>{consultant && consultant.displayName ? consultant.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('Customer')}>
                            <Label className={styles.label}>{customer && customer.displayName ? customer.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('Project')}>
                            <Label className={styles.label}>{project && project.displayName ? project.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('ProjectsAssigment')} >
                            <Label className={styles.label}>{projectAssignment && projectAssignment.displayName ? projectAssignment.displayName : "-"}</Label>
                        </Field>
                        <Field label="Date">
                            <Label className={styles.label}>{timeSheetDelete && timeSheetDelete.date ? new Date(timeSheetDelete.date).toLocaleDateString() : "-"}</Label>
                        </Field>
                        <Field label="Description">
                            <Label className={styles.label}>{timeSheetDelete && timeSheetDelete.description ? timeSheetDelete.description : ""}</Label>
                        </Field>
                        <Field label={t('Quantity')}>
                            <Label className={styles.label}>{timeSheetDelete ? timeSheetDelete.count : ""}</Label>
                        </Field>
                        <Field label={t('InvoiceRef')}>
                            <Label className={styles.label}>{invoice && invoice.invoiceRef ? invoice.invoiceRef : ""}</Label>
                        </Field>
                        <Switch checked={confirmDelete} label={t('ConfirmDelete')} onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmDelete(data.checked); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestTimeSheetEntrydeletion(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!confirmDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};